import axios from 'axios';
import Qs from 'qs';
import { recordCall } from './pageTiming';
import { defaultConfig } from '../config';
import { apiUrls } from './apiUrls';
import { store } from '../store/store';
import { showLoader, hideLoader } from '../store/slices/loaderSlice';

const serializerConfig = {
  arrayFormat: 'brackets',
  encode: false,
};

const handleLogout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('orderart_session_id');
  localStorage.removeItem('order_id');
  localStorage.removeItem('orderType');

  window.location.href = '/';
};

const refreshAccessToken = async () => {
  try {
    const API_ROOT = defaultConfig.baseAPIUrl;
    const refreshToken = localStorage.getItem("refreshToken");
    const userId = localStorage.getItem('userId');

    const response = await axios({
      method: 'post',
      url: API_ROOT + apiUrls.REFRESH_TOKEN_URL,
      data: { refresh_token: refreshToken, userId },
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.data?.access_token) {
      localStorage.setItem("accessToken", response.data.access_token);
      localStorage.setItem("idToken", response.data.IdToken);
      return response.data.access_token;
    }

    return false;

  } catch (error) {
    console.error("Failed to refresh access token:", error);
    throw error;
  }
};

const sameBody = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

// The offline engine answers a few live reads from what the device holds and says so (X-OA-Refreshing: 1) while it asks the
// server in the background. Asking again with X-OA-Wait-Fresh: 1 returns the refreshed answer as soon as it arrives; the page is
// told only when it differs from what it already shows. Never throws, never shows the loader, and says nothing on failure.
async function followUpFresh(request, first, onFresh) {
  try {
    const next = await axios({ ...request, headers: { ...request.headers, 'X-OA-Wait-Fresh': '1' } });
    if (next.status >= 200 && next.status < 300 && !sameBody(first.data, next.data)) onFresh(next);
  } catch {
    /* the first answer stays on screen */
  }
}

/**
 * extra.onFresh(response): called, at most once, with a newer answer to the same read when the first one came from the device's
 * copy and the engine was already refreshing it. Callers use it to update what they showed from the first answer.
 */
async function callAPI(path, params = {}, method = 'get', data = null, options = {}, headersObj = {},   withLoader = true, extra = {}  ) {

  const API_ROOT = defaultConfig.baseAPIUrl;
  const url = API_ROOT + path;

  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem("accessToken");

  const headers = {
    Accept: 'application/json',
    'x-user-id': userId,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...headersObj
  };

  params = {
    ...params,
    _cacheBuster: Date.now()
  };

  const sessionId = localStorage.getItem('sessionId');
  if (sessionId) {
    params.sessionId = sessionId;
  }

  const shouldShowLoader = withLoader;

  if (shouldShowLoader) {
    store.dispatch(showLoader());
  }

  // for Settings > Performance: how long this call took, and who answered it
  const startedAt = performance.now();
  let outcomeStatus = 0;
  let outcomeSource = 'cloud';

  try {

    const response = await axios({
      method,
      url,
      params,
      paramsSerializer: (p) => Qs.stringify(p, serializerConfig),
      data,
      headers,
      ...options,
    });

    outcomeStatus = response.status;
    outcomeSource = response.headers?.['x-oa-source'] || 'cloud';
    if (typeof extra?.onFresh === 'function' && String(response.headers?.['x-oa-refreshing']) === '1') {
      followUpFresh({ method, url, params, paramsSerializer: (p) => Qs.stringify(p, serializerConfig), data, headers, ...options }, response, extra.onFresh);
    }
    return response;

  } catch (error) {

    outcomeStatus = error.response?.status ?? 0;

    if (error.response?.status === 401 && !options._retry) {

      options._retry = true;

      try {
        const newToken = await refreshAccessToken();

        if (newToken) {

          headers.Authorization = `Bearer ${newToken}`;

          const retryResponse = await axios({
            method,
            url,
            params,
            paramsSerializer: (p) => Qs.stringify(p, serializerConfig),
            data,
            headers,
            ...options,
          });

          outcomeStatus = retryResponse.status;
          return retryResponse;
        }

      } catch (refreshError) {
        console.error("Refresh failed:", refreshError);
        handleLogout();
      }
    }

    throw error;
  }  finally {

    if (shouldShowLoader) {
      store.dispatch(hideLoader());
   }

    recordCall({ path, method, start: startedAt, ms: performance.now() - startedAt, status: outcomeStatus, source: outcomeSource });

  }

}

export { callAPI, refreshAccessToken };